import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackButtonClick } from '../helpers/tlfAnalytics';

// Hooks
import { useGameHomeData } from '../hooks/useGameHomeData';
import { usePurchaseNavigate } from '../hooks/usePurchaseNavigate';
import { useSectionVisibility } from '../hooks/useSectionVisibility';
import useDrawMetrics from '../hooks/useDrawMetrics';
import useHomeSlides from '../hooks/useHomeSlides';
import { usePageSections } from '../hooks/usePageSections';
import { useLayoutStyle } from '../contexts/LayoutStyleContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useGameType } from '../contexts/GameTypeContext';
import { useEditMode } from '../contexts/EditModeContext';
import { useContent } from '../contexts/ContentContext';
import { useCountdown } from '../hooks/useCountdown';
import { useCountdownStyle } from '../contexts/CountdownStyleContext';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { isDrawClosed as checkDrawClosed } from '../utils/drawLifecycle';
import { SectionControlProvider } from '../contexts/SectionControlContext';

// Grid layout engine
import GridEngine from '../components/GridEngine';
import { useGridLayout } from '../hooks/useGridLayout';
import { renderSection as renderGridSection } from '../utils/sectionRegistry.jsx';
import { getStyleSectionRenderer } from '../styles/sections';
import SectionItem from '../components/SectionItem';

// Error boundary
import ErrorBoundary from '../components/ErrorBoundary';

// Content editing
import InlineTranslationEdit from '../components/InlineTranslationEdit';
import { 
  HomeHowItWorks, HomeHowItWorksLearnMore, HomeStandardCTAButton,
  HomeOperatedBy, HomeParticipatingCaption, HomeAdditionalDrawsTitle,
  HomeBannerText, HomeJackpot, HomeCutOffDatePassed
} from '../content/keys';

// Configs
import { getGameTypeConfig, isSectionEnabled, getGameTypeSlides } from '../config/gameTypes';
import { getLayoutStyleConfig, getGridLayoutClasses } from '../config/layoutStyles';

// Layout components
import GameHomeLayout from '../components/layout/GameHomeLayout';
import HomeSidebar from '../components/layout/HomeSidebar';

// Critical-path layout components (eagerly loaded to avoid LCP waterfall)
// These are the default layouts that render on landing page first paint.
// Default config: gameType=chaseTheAce, layoutStyle=style1 → ChaseTheAceStyle1
// ChaseTheAceStyleStandard is the fallback for unknown chaseTheAce styles.
const ChaseTheAceStyle1 = lazy(() => import('./layouts/ChaseTheAceStyle1'));
const ChaseTheAceStyleStandard = lazy(() => import('./layouts/ChaseTheAceStyleStandard'));

// Non-critical layout components (lazy-loaded — only one renders at a time)
const Classic5050Style5 = lazy(() => import('./layouts/Classic5050Style5'));
const Classic5050StylePremium = lazy(() => import('./layouts/Classic5050StylePremium'));
const Classic5050StylePremiumPlus = lazy(() => import('./layouts/Classic5050StylePremiumPlus'));
const Classic5050StyleStandard = lazy(() => import('./layouts/Classic5050StyleStandard'));
const Classic5050Style1 = lazy(() => import('./layouts/Classic5050Style1'));
const Classic5050Style2 = lazy(() => import('./layouts/Classic5050Style2'));
const Classic5050Style3 = lazy(() => import('./layouts/Classic5050Style3'));
const Classic5050Style4 = lazy(() => import('./layouts/Classic5050Style4'));
const Classic5050StyleFluid = lazy(() => import('./layouts/Classic5050StyleFluid'));
const Modified5050StyleStandard = lazy(() => import('./layouts/Modified5050StyleStandard'));
const Modified5050Style1 = lazy(() => import('./layouts/Modified5050Style1'));
const SuperballBingoStyle1 = lazy(() => import('./layouts/SuperballBingoStyle1'));
const SuperballBingoStyle2 = lazy(() => import('./layouts/SuperballBingoStyle2'));
const SuperballBingoStyle3 = lazy(() => import('./layouts/SuperballBingoStyle3'));
const SuperballBingoStyleStandard = lazy(() => import('./layouts/SuperballBingoStyleStandard'));
const SuperballBingoStyleLiveBoard = lazy(() => import('./layouts/SuperballBingoStyleLiveBoard'));
const ChaseTheAceStyleDeckRunner = lazy(() => import('./layouts/ChaseTheAceStyleDeckRunner'));
const ChaseTheAceStyleStorybook = lazy(() => import('./layouts/ChaseTheAceStyleStorybook'));
const PremiumLogoAdminPanel = lazy(() => import('../components/PremiumLogoAdminPanel'));
const EliminationStyle1 = lazy(() => import('./layouts/EliminationStyle1'));
const EliminationStyle2 = lazy(() => import('./layouts/EliminationStyle2'));
const EliminationStyleStandard = lazy(() => import('./layouts/EliminationStyleStandard'));
const Progressive5050Style2 = lazy(() => import('./layouts/Progressive5050Style2'));
const Progressive5050Style3 = lazy(() => import('./layouts/Progressive5050Style3'));
const Progressive5050StyleStandard = lazy(() => import('./layouts/Progressive5050StyleStandard'));
const SquareGameStyleStandard = lazy(() => import('./layouts/SquareGameStyleStandard'));
const SquareGameStyleScoreboard = lazy(() => import('./layouts/SquareGameStyleScoreboard'));
const SquareGameStyleCharity = lazy(() => import('./layouts/SquareGameStyleCharity'));
const PrizeDrawStyle1 = lazy(() => import('./layouts/PrizeDrawStyle1'));
const PrizeDrawStyleGallerySpotlight = lazy(() => import('./layouts/PrizeDrawStyleGallerySpotlight'));
const PrizeDrawStyleStandard = lazy(() => import('./layouts/PrizeDrawStyleStandard'));
const CinematicLayout = lazy(() => import('./layouts/CinematicLayout'));

// Section components
import HeroSection from '../components/sections/HeroSection';
import FeatureCarousel from '../components/sections/FeatureCarousel';
import InfoCallouts from '../components/sections/InfoCallouts';
import { SectionRenderer, SectionList } from '../components/sections/SectionRenderer';

// Existing components
import LiveDrawCard from '../components/LiveDrawCard';
import PartnerBanner from '../components/PartnerBanner';
import ShareModal from '../components/ShareModal';
import ContentBlockSection from '../components/ContentBlockSection';
import LazySection from '../components/LazySection';
const DrawMetricsCard = lazy(() => import('../components/sections/DrawMetricsCard'));
const BonusDrawsGrid = lazy(() => import('../components/sections/BonusDrawsGrid'));
const RecentWinners = lazy(() => import('../components/RecentWinners'));
const Leaderboard = lazy(() => import('../components/Leaderboard'));
const WinnersBanner = lazy(() => import('../components/WinnersBanner'));
const SectionManager = lazy(() => import('../components/SectionManager'));

// Charity components
import SpacerSection from '../components/SpacerSection';
import DynamicSections from '../components/DynamicSections';

const SplitPotCharity = lazy(() => import('../components/charity/SplitPotCharity'));
const GrowingImpactCharity = lazy(() => import('../components/charity/GrowingImpactCharity'));
const AceOfHeartsCharity = lazy(() => import('../components/charity/AceOfHeartsCharity'));
const GiftBoxCharity = lazy(() => import('../components/charity/GiftBoxCharity'));
const SurvivorShieldCharity = lazy(() => import('../components/charity/SurvivorShieldCharity'));
const CenterSquareCharity = lazy(() => import('../components/charity/CenterSquareCharity'));
const SuperballBingoCharity = lazy(() => import('../components/charity/SuperballBingoCharity'));
const CharityHeroStrip = lazy(() => import('../components/CharityHeroSection').then((module) => ({ default: module.CharityHeroStrip })));
const CharitySidebar = lazy(() => import('../components/CharityHeroSection').then((module) => ({ default: module.CharitySidebar })));

// Map charity component names to actual components
const CHARITY_COMPONENTS = {
  SplitPotCharity,
  GrowingImpactCharity,
  AceOfHeartsCharity,
  GiftBoxCharity,
  SurvivorShieldCharity,
  CenterSquareCharity,
  CharitySidebar: CenterSquareCharity, // Alias for squareGame
  SuperballBingoCharity,
};

/**
 * GameHomePage - Unified homepage component for all game types
 * 
 * Replaces 6 individual homepage files by using configuration-driven rendering.
 * 
 * @param {Object} props
 * @param {string} props.gameType - Required. One of: 'classic5050', 'progressive5050', 'modified5050', 'eliminationDraw', 'chaseTheAce', 'prizeDraw', 'squareGame', 'superballBingo'
 * @param {Function} props.onLoginClick - Optional callback for login CTA
 * @param {string} props.partnerId - Optional partner ID for filtering
 * @param {boolean} props.isAdmin - Optional admin flag override
 */
function GameHomePageInner({ 
  gameType, 
  onLoginClick,
  partnerId,
  isAdmin: isAdminProp 
}) {
  const navigate = useNavigate();
  const { changeGameType } = useGameType();
  
  // Sync GameTypeContext with the gameType prop so downstream pages
  // (e.g. /purchase via useGameType()) render the correct game type.
  useEffect(() => {
    changeGameType(gameType);
  }, [gameType, changeGameType]);
  
  // Get configurations
  const gameConfig = useMemo(() => getGameTypeConfig(gameType), [gameType]);
  const { layoutStyle } = useLayoutStyle();
  const { language } = useLanguage();
  const layoutConfig = useMemo(() => getLayoutStyleConfig(layoutStyle), [layoutStyle]);
  
  // Grid layout engine (feature-flagged)
  const { gridConfig, isGridEngine } = useGridLayout(layoutStyle, gameType);
  
  // Get page sections key from game config
  const pageSectionKey = gameConfig?.pageSectionKey || `${gameType}_home`;
  
  // Hooks for data and state
  const { mainDraw, bonusDraws, eliminatedCards, deck, partner, sponsor, prizes, winners, loading, error, refetch } = useGameHomeData(gameType, { partnerId });
  const { loading: siteConfigLoading = true, siteDetails, nextDraw, isDrawClosed } = useSiteConfig();
  const combinedLoading = loading || !!siteConfigLoading;
  const { sections, updateSection, ensureSectionType, error: sectionSyncError } = usePageSections(pageSectionKey);
  const { isSectionVisible, getSortedSections } = useSectionVisibility(sections);
  const { ticketsRemaining, percentageRemaining, isLowStock, isSoldOut } = useDrawMetrics(mainDraw);
  const homeSlides = useHomeSlides(mainDraw, { gameType });

  // Tracks whether ALL async data (siteConfig + draw data) has ever finished loading.
  // Once true, it stays true — subsequent refetches never flash back to the skeleton.
  // This is the single gate that prevents the layout from mounting in a half-loaded
  // state, eliminating the skeleton→blank→content flicker chain.
  const [dataEverLoaded, setDataEverLoaded] = useState(false);
  useEffect(() => {
    if (!combinedLoading && !dataEverLoaded) {
      setDataEverLoaded(true);
    }
  }, [combinedLoading, dataEverLoaded]);

  // Signal to CSS and tooling (PageSpeed, Lighthouse) that the page is fully loaded.
  useEffect(() => {
    if (dataEverLoaded) {
      document.documentElement.setAttribute('data-loaded', '');
    }
    return () => {
      document.documentElement.removeAttribute('data-loaded');
    };
  }, [dataEverLoaded]);

  // Edit mode from context
  const { editMode, handleTranslationUpdate, handleSave, isSaving, isAdmin: isAdminContext } = useEditMode();
  const isAdmin = isAdminProp ?? isAdminContext;

  // Content translations
  const { c: t } = useContent();

  // Countdown for GridEngine sections (computed once, passed as props)
  const { timeLeft, shouldAnimate } = useCountdown(mainDraw, { enableAnimation: true });
  const { countdownStyle } = useCountdownStyle();

  // Local state
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const renderSectionPlaceholder = useCallback((fallbackClassName = 'h-48') => (
    <div className={`${fallbackClassName} animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800`} />
  ), []);

  const renderSuspendedSection = useCallback((content, fallbackClassName = 'h-48') => (
    <Suspense fallback={renderSectionPlaceholder(fallbackClassName)}>
      {content}
    </Suspense>
  ), [renderSectionPlaceholder]);

  const renderDeferredSection = useCallback((content, fallbackClassName = 'h-48') => (
    <LazySection
      disabled={editMode || isAdmin || dataEverLoaded}
      fallback={renderSectionPlaceholder(fallbackClassName)}
    >
      <Suspense fallback={renderSectionPlaceholder(fallbackClassName)}>
        {content}
      </Suspense>
    </LazySection>
  ), [editMode, isAdmin, dataEverLoaded, renderSectionPlaceholder]);

  // Resolve the charity component from game config
  const CharityComponent = useMemo(() => {
    const componentName = gameConfig?.charityComponent;
    return componentName ? CHARITY_COMPONENTS[componentName] : null;
  }, [gameConfig]);

  const purchaseNavigate = usePurchaseNavigate(onLoginClick);

  // Navigation handlers
  const salesClosed = (!siteConfigLoading && isDrawClosed)
    || checkDrawClosed({ nextDraw: mainDraw || nextDraw, siteDetails })
    || !!mainDraw?.cutoff_date_passed;

  const handleBuyNowClick = useCallback(() => {
    if (salesClosed) {
      navigate('/closed');
      return;
    }
    trackButtonClick('buy-now', 'Buy Now', { gameType });
    trackCheckoutStart({ gameType, lotteryId: mainDraw?.id });
    const purchaseRoute = gameConfig?.purchaseRoute || '/purchase';
    purchaseNavigate(purchaseRoute);
  }, [purchaseNavigate, gameConfig, salesClosed, gameType, mainDraw?.id, navigate]);

  const handleShareClick = useCallback(() => {
    setShareModalOpen(true);
  }, []);

  const handleDrawClick = useCallback((draw) => {
    if (draw?.id) {
      navigate(`/draw/${draw.id}`);
    }
  }, [navigate]);

  // Derive slides from game config (combined with layout config gradient fallback)
  const slides = useMemo(() => {
    const gameSlides = getGameTypeSlides(gameType);
    return gameSlides.map(slide => ({
      ...slide,
      bgGradient: slide.bgGradient || gameConfig?.themeColors?.gradient,
    }));
  }, [gameType, gameConfig]);

  // Map section types to components for SectionRenderer
  const sectionComponents = useMemo(() => ({
    hero_carousel: () => (
      <FeatureCarousel 
        slides={slides}
        autoRotate={true}
        interval={5000}
      />
    ),
    charity_banner: () => (
      CharityComponent ? renderDeferredSection(
        <CharityComponent 
          editMode={editMode}
          onTranslationUpdate={handleTranslationUpdate}
        />,
        'h-56'
      ) : null
    ),
    recent_winners: ({ config }) => (
      renderDeferredSection(
        <RecentWinners 
          editMode={editMode}
          onTranslationUpdate={handleTranslationUpdate}
          limit={config?.limit || 5}
        />,
        'h-56'
      )
    ),
    leaderboard: ({ config, section } = {}) => {
      const resolvedSection = section || sections?.find(s => s.section_type === 'leaderboard');
      return layoutConfig.showTopPartners !== false ? renderDeferredSection(
        <Leaderboard 
          editMode={editMode}
          onTranslationUpdate={handleTranslationUpdate}
          period={config?.period || resolvedSection?.config?.period || 'currentDraw'}
          limit={config?.limit || resolvedSection?.config?.limit || 10}
          showBadges={(config?.showBadges ?? resolvedSection?.config?.showBadges) !== false}
          showPlacement={(config?.showPlacement ?? resolvedSection?.config?.showPlacement ?? config?.showBadges ?? resolvedSection?.config?.showBadges) !== false}
          showSupporters={(config?.showSupporters ?? resolvedSection?.config?.showSupporters) !== false}
          showTickets={(config?.showTickets ?? resolvedSection?.config?.showTickets) !== false}
          showAmount={(config?.showAmount ?? resolvedSection?.config?.showAmount) !== false}
          displayStyle={config?.displayStyle || resolvedSection?.config?.displayStyle || 'list'}
          sortBy={config?.sortBy || resolvedSection?.config?.sortBy || 'amount'}
          section={resolvedSection}
          updateSection={updateSection}
        />,
        'h-72'
      ) : null;
    },
    bonus_draws: () => (
      layoutConfig.showBonusDraws !== false && isSectionVisible('bonus_draws') && bonusDraws.length > 0 ? renderDeferredSection(
        <BonusDrawsGrid 
          draws={bonusDraws}
          onDrawClick={handleDrawClick}
          editMode={editMode}
          handleTranslationUpdate={handleTranslationUpdate}
        />,
        'h-56'
      ) : null
    ),
    content_block_1: () => <ContentBlockSection sectionType="content_block_1" />,
    content_block_2: () => <ContentBlockSection sectionType="content_block_2" />,
    content_block_3: () => <ContentBlockSection sectionType="content_block_3" />,
    charity_hero: () => renderDeferredSection(<CharitySidebar />, 'h-56'),
    charity_strip: () => (
      renderDeferredSection(
        <CharityHeroStrip
          editMode={editMode}
          onTranslationUpdate={handleTranslationUpdate}
        />,
        'h-40'
      )
    ),
    spacer_small: () => <SpacerSection sectionType="spacer_small" sections={sections} />,
    spacer_medium: () => <SpacerSection sectionType="spacer_medium" sections={sections} />,
    spacer_large: () => <SpacerSection sectionType="spacer_large" sections={sections} />,
    winners_banner: ({ config }) => (
      renderDeferredSection(
        <WinnersBanner
          editMode={editMode}
          gameType={gameType}
          maxWinners={config?.maxWinners || 3}
        />,
        'h-48'
      )
    ),
  }), [slides, CharityComponent, editMode, handleTranslationUpdate, bonusDraws, isSectionVisible, handleDrawClick, layoutConfig, gameType, renderDeferredSection]);

  // Render hero section based on layout config
  const renderHero = () => {
    if (!layoutConfig.showHero) {
      // For non-hero layouts, show charity hero strip if enabled
      if (layoutConfig.showCharityHeroStrip && CharityComponent) {
        return renderSuspendedSection(
          <CharityComponent 
            variant="strip"
            editMode={editMode}
            onTranslationUpdate={handleTranslationUpdate}
          />,
          'h-40'
        );
      }
      return null;
    }

    // Hero carousel/feature carousel for hero layouts
    if (layoutConfig.showCarousel && slides.length > 0) {
      return (
        <FeatureCarousel 
          slides={slides}
          autoRotate={true}
          interval={5000}
          className="mb-6"
        />
      );
    }

    // Simple hero section
    const heroConfig = gameConfig?.heroConfig || {};
    return (
      <HeroSection
        title={heroConfig.title}
        subtitle={heroConfig.subtitle}
        bgGradient={gameConfig?.themeColors?.gradient}
        ctaLabel={t(HomeStandardCTAButton, 'Buy Tickets')}
        onCtaClick={handleBuyNowClick}
        drawDate={mainDraw}
      />
    );
  };

  // Render metrics card based on layout
  const renderMetricsCard = () => {
    if (!layoutConfig.showMetricsCard) return null;

    return renderDeferredSection(
      <DrawMetricsCard
        draw={mainDraw}
        className="mb-6"
      />,
      'h-40'
    );
  };

  // Render how-it-works callouts
  const renderHowItWorks = () => {
    if (!layoutConfig.showHowItWorks) return null;

    const howItWorks = gameConfig?.howItWorks;
    const steps = gameConfig?.steps;

    if (steps?.length > 0) {
      return (
        <InfoCallouts
          title={howItWorks?.heading}
          callouts={steps.map(step => ({
            icon: step.icon,
            title: step.title,
            description: step.description,
            bgColor: 'purple',
          }))}
          layout="grid"
          className="mb-6"
        />
      );
    }

    if (howItWorks?.description) {
      return (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-2xl p-6 mb-6">
          <h3 className="text-lg font-bold text-purple-900 dark:text-purple-100 mb-2">
            <InlineTranslationEdit
              translationKey={HomeHowItWorks}
              value={t(HomeHowItWorks, howItWorks.heading)}
              fallback={howItWorks.heading}
            />
          </h3>
          <p className="text-purple-800 dark:text-purple-200">
            <InlineTranslationEdit
              translationKey={HomeHowItWorksLearnMore}
              value={t(HomeHowItWorksLearnMore, howItWorks.description)}
              fallback={howItWorks.description}
            />
          </p>
        </div>
      );
    }

    return null;
  };

  // Render main content sections
  const renderMainContent = () => {
    const sortedSections = getSortedSections();
    
    // Determine which section types to render - exclude charity_banner if already shown in hero strip
    const charityAlreadyShown = !layoutConfig.showHero && layoutConfig.showCharityHeroStrip && CharityComponent;
    // Exclude section types already rendered elsewhere (hero, sidebar, bonus_draws below)
    const excludeTypes = [
      'hero_carousel', 'hero_section', 'image_slider', 'organization_writeup',
      'live_draw', 'winners_banner', 'partner_banner', 'bonus_draws',
      ...(charityAlreadyShown ? ['charity_banner'] : []),
    ];
    
    // Allow ALL section types except those rendered in other zones
    const filterTypes = sortedSections
      .map(s => s.section_type)
      .filter(type => !excludeTypes.includes(type));
    
    return (
      <>
        {/* How It Works - if enabled */}
        {renderHowItWorks()}
        
        {/* Metrics Card - if enabled */}
        {renderMetricsCard()}

        {/* Dynamic sections from page config */}
        <SectionList
          sections={sortedSections}
          components={sectionComponents}
          editMode={editMode}
          onTranslationUpdate={handleTranslationUpdate}
          filterTypes={filterTypes}
          className="space-y-6"
        />

        {/* Catch-all for section types not in sectionComponents */}
        <DynamicSections
          sections={sortedSections}
          knownTypes={[...excludeTypes, ...Object.keys(sectionComponents)]}
          editMode={editMode}
        />

        {/* Bonus Draws - if enabled by layout and has draws */}
        {layoutConfig.showBonusDraws !== false && isSectionVisible('bonus_draws') && bonusDraws.length > 0 && renderDeferredSection(
          <BonusDrawsGrid 
            draws={bonusDraws}
            onDrawClick={handleDrawClick}
            editMode={editMode}
            handleTranslationUpdate={handleTranslationUpdate}
            className="mt-6"
          />,
          'h-56'
        )}
      </>
    );
  };

  // Render sidebar content
  const renderSidebar = () => {
    const showLiveDrawCard = layoutConfig.showLiveDrawCard !== false;
    const showPartnerBanner = layoutConfig.showPartnerBanner !== false && partner;
    const showWinnersBanner = layoutConfig.showWinnersBanner !== false && isSectionVisible('winners_banner');

    return (
      <HomeSidebar sticky={true}>
        {/* Live Draw Card */}
        {showLiveDrawCard && isSectionVisible('live_draw') && (
          <LiveDrawCard
            draw={mainDraw}
            gameType={gameType}
            onLoginClick={onLoginClick}
            onBuyNowClick={handleBuyNowClick}
            onShareClick={handleShareClick}
            hasAdditionalDraws={bonusDraws.length > 0}
            editMode={editMode}
            onTranslationUpdate={handleTranslationUpdate}
            hideCountdown={false}
          />
        )}

        {/* Partner Banner */}
        {showPartnerBanner && (
          <PartnerBanner
            partner={partner}
            editMode={editMode}
            onTranslationUpdate={handleTranslationUpdate}
          />
        )}

        {/* Winners Banner */}
        {showWinnersBanner && renderDeferredSection(
          <WinnersBanner
            editMode={editMode}
            gameType={gameType}
          />,
          'h-48'
        )}
      </HomeSidebar>
    );
  };

  // Render footer content (admin section manager + modals)
  const renderFooter = () => (
    <>
      {/* Section Manager for admins */}
      {isAdmin && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Suspense fallback={null}>
            <SectionManager
              pageName={pageSectionKey}
              sections={sections}
              onUpdate={updateSection}
              gameType={gameType}
            />
          </Suspense>
          {sectionSyncError && (
            <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-300 px-4 py-2 text-sm text-amber-800">
              <svg className="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <span>{sectionSyncError}</span>
            </div>
          )}
        </div>
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        draw={mainDraw}
        gameType={gameType}
      />
    </>
  );

  // Build section config map for GridEngine (bridges admin controls to grid properties + section_order for reordering)
  // NOTE: This useMemo MUST stay above all early returns to satisfy React's Rules of Hooks.
  const gridSectionConfigs = useMemo(() => {
    if (!sections) return undefined
    const map = {}
    for (const s of sections) {
      if (s.section_type) {
        map[s.section_type] = s
      }
    }
    return map
  }, [sections])

  // Error state
  if (error && !mainDraw) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Unable to load draw data
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Please try again later or contact support.
          </p>
          <button
            onClick={refetch}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Gate: don't render any layout until ALL data (SiteConfig + draw data) has
  // finished loading at least once. After that, the layout stays mounted even
  // during refetches — no more skeleton→blank→content flicker chain.
  if (!dataEverLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <div className="flex flex-col items-center gap-6">
          {/* Orbital ring spinner */}
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-[3px] border-gray-100 dark:border-gray-800" />
            <div
              className="absolute inset-0 rounded-full border-[3px] border-transparent animate-[spin_0.8s_linear_infinite]"
              style={{
                borderTopColor: 'var(--charity-primary, #7c3aed)',
                borderRightColor: 'var(--charity-accent, #a78bfa)',
              }}
            />
          </div>
          {/* Subtle label */}
          <p className="text-sm font-medium tracking-wide text-gray-400 dark:text-gray-500 animate-pulse">
            Getting things ready
          </p>
        </div>
      </div>
    );
  }

  // Determine grid layout based on style
  const gridClasses = getGridLayoutClasses(layoutStyle);
  const isFullWidth = gridClasses.sidebar === null;

  // Helper: wrap any dedicated layout in SectionControlProvider
  const withSections = (el, { inGrid = false } = {}) => (
    <SectionControlProvider sections={sections} updateSection={updateSection} ensureSectionType={ensureSectionType} gridColumns={gridConfig?.columns || null} insideGridEngine={inGrid}>
      <Suspense fallback={<div className="min-h-screen" />}>
        {el}
      </Suspense>
    </SectionControlProvider>
  );

  // --- Grid Layout Engine path (feature-flagged, Phase 1: all game types) ---
  // When enabled and a matching template/custom config exists, render via GridEngine
  // instead of the dedicated JSX layout files below.
  if (isGridEngine && gridConfig) {
    const gridSectionProps = {
      mainDraw,
      bonusDraws,
      partner,
      loading: combinedLoading,
      editMode,
      handleTranslationUpdate,
      sections,
      updateSection,
      salesClosed,
      t,
      c: t,
      language,
      onLoginClick,
      gameType,
      isAdmin,
      slides: homeSlides,
      layoutStyle,
      purchaseNavigate,
      timeLeft,
      shouldAnimate,
      countdownStyle,
      // Chase the Ace specific (parsed from winners by useGameHomeData; falls back to mainDraw.eliminated_cards inside the hook)
      eliminatedCards: eliminatedCards || [],
      deck: deck || [],
      // Prize Draw specific
      prizes: mainDraw?.prizes || [],
      // Square Game specific
      squares: mainDraw?.squares || [],
      // Superball Bingo specific
      calledBalls: mainDraw?.called_balls || [],
      sessionData: mainDraw?.session_data || {},
    }

    const gridRenderSection = (sectionType, context) => {
      // Check visibility from page-sections config
      const sectionData = sections?.find(s => s.section_type === sectionType)
      if (!sectionData || (!sectionData.is_visible && !editMode)) return null

      // Try style-specific renderer first, fall back to generic sectionRegistry
      const StyleComponent = getStyleSectionRenderer(gameType, layoutStyle, sectionType)
      let content
      if (StyleComponent) {
        content = <StyleComponent {...gridSectionProps} {...context} />
      } else {
        content = renderGridSection(sectionType, {
          ...gridSectionProps,
          ...context,
        })
      }

      if (!content) return null

      return (
        <SectionItem
          sectionType={sectionType}
          editMode={editMode}
          insideGridEngine
        >
          {content}
        </SectionItem>
      )
    }

    return withSections(
      <main className="min-h-screen">
        {isAdmin && (
          <Suspense fallback={null}>
            <SectionManager sections={sections} onUpdate={updateSection} gridColumns={gridConfig?.columns || null} insideGridEngine gameType={gameType} />
          </Suspense>
        )}
        {isAdmin && (layoutStyle === 'premium' || layoutStyle === 'premium-plus' || layoutStyle === 'storybook') && (
          <Suspense fallback={null}>
            <PremiumLogoAdminPanel sections={sections} updateSection={updateSection} />
          </Suspense>
        )}
        <GridEngine
          layout={gridConfig}
          renderSection={gridRenderSection}
          editMode={editMode}
          sectionConfigs={gridSectionConfigs}
        />
      </main>,
      { inGrid: true }
    )
  }

  // Use dedicated layout component for style5 (Theater Marquee)
  if (layoutStyle === 'style5') {
    return withSections(
      <Classic5050Style5
        mainDraw={mainDraw}
        bonusDraws={bonusDraws}
        loading={combinedLoading}
        partner={partner}
        onLoginClick={onLoginClick}
        t={t}
        editMode={editMode}
        pendingChanges={{}}
        isSaving={isSaving}
        handleTranslationUpdate={handleTranslationUpdate}
        handleSave={handleSave}
        isAdmin={isAdmin}
        sections={sections}
        updateSection={updateSection}
        refetch={refetch}
      />
    );
  }

  // Cross-game layouts: Cinematic, Fluid, Premium, PremiumPlus work for ALL game types
  const crossGameProps = {
    mainDraw, bonusDraws, loading: combinedLoading, partner, sponsor, onLoginClick, t, editMode,
    pendingChanges: {}, isSaving, handleTranslationUpdate, handleSave,
    isAdmin, sections, updateSection, salesClosed, gameType, refetch,
  };

  if (layoutStyle === 'cinematic') {
    return withSections(
      <CinematicLayout
        {...crossGameProps}
        eliminatedCards={eliminatedCards}
        deck={deck}
        winners={winners}
        prizes={prizes}
      />
    );
  }
  if (layoutStyle === 'fluid') {
    return withSections(<Classic5050StyleFluid {...crossGameProps} />);
  }
  if (layoutStyle === 'premium' && gameType !== 'chaseTheAce' && gameType !== 'eliminationDraw') {
    // Chase/Elimination have their own premium (style1) variants
    return withSections(<Classic5050StylePremium {...crossGameProps} />);
  }
  if (layoutStyle === 'premium-plus' && gameType !== 'progressive5050') {
    // Progressive already handles premium-plus in its own block
    return withSections(<Classic5050StylePremiumPlus {...crossGameProps} />);
  }

  if (gameType === 'superballBingo') {
    const layoutProps = {
      mainDraw,
      bonusDraws,
      loading: combinedLoading,
      partner,
      onLoginClick,
      t,
      editMode,
      pendingChanges: {},
      isSaving,
      handleTranslationUpdate,
      handleSave,
      isAdmin,
      sections,
      updateSection,
      salesClosed,
      refetch,
    };

    if (layoutStyle === 'standard') {
      return withSections(<SuperballBingoStyleStandard {...layoutProps} />);
    }
    if (layoutStyle === 'style1') {
      return withSections(<SuperballBingoStyle1 {...layoutProps} />);
    }
    if (layoutStyle === 'style2') {
      return withSections(<SuperballBingoStyle2 {...layoutProps} />);
    }
    if (layoutStyle === 'style3') {
      return withSections(<SuperballBingoStyle3 {...layoutProps} />);
    }
    if (layoutStyle === 'liveBoard') {
      return withSections(<SuperballBingoStyleLiveBoard {...layoutProps} />);
    }
    // charity falls through to generic GameHomeLayout (CharityComponent hero)
    if (layoutStyle !== 'charity') {
      // Default to style1 for superball bingo
      return withSections(<SuperballBingoStyle1 {...layoutProps} />);
    }
  }

  // Chase the Ace dedicated layouts
  if (gameType === 'chaseTheAce') {
    const layoutProps = {
      mainDraw,
      bonusDraws,
      loading: combinedLoading,
      partner,
      eliminatedCards,
      deck,
      winners,
      onLoginClick,
      t,
      editMode,
      pendingChanges: {},
      isSaving,
      handleTranslationUpdate,
      handleSave,
      isAdmin,
      sections,
      updateSection,
      salesClosed,
      refetch,
    };

    if (layoutStyle === 'standard') {
      return withSections(<ChaseTheAceStyleStandard {...layoutProps} />);
    }
    if (layoutStyle === 'style1') {
      return withSections(<ChaseTheAceStyle1 {...layoutProps} />);
    }
    if (layoutStyle === 'premium') {
      return withSections(<ChaseTheAceStyleStorybook {...layoutProps} />);
    }
    if (layoutStyle === 'deckRunner') {
      return withSections(<ChaseTheAceStyleDeckRunner {...layoutProps} />);
    }
    if (layoutStyle === 'storybook') {
      return withSections(<ChaseTheAceStyleStorybook {...layoutProps} />);
    }
    // charity falls through to generic GameHomeLayout (CharityComponent hero)
    if (layoutStyle !== 'charity') {
      return withSections(<ChaseTheAceStyleStandard {...layoutProps} />);
    }
  }

  // Elimination dedicated layouts
  if (gameType === 'eliminationDraw') {
    const layoutProps = {
      mainDraw,
      bonusDraws,
      loading: combinedLoading,
      partner,
      onLoginClick,
      t,
      editMode,
      pendingChanges: {},
      isSaving,
      handleTranslationUpdate,
      handleSave,
      isAdmin,
      sections,
      updateSection,
      salesClosed,
      refetch,
    };

    if (layoutStyle === 'style1' || layoutStyle === 'premium') {
      return withSections(<EliminationStyle1 {...layoutProps} />);
    }
    if (layoutStyle === 'style2') {
      return withSections(<EliminationStyle2 {...layoutProps} />);
    }
    if (layoutStyle === 'standard') {
      return withSections(<EliminationStyleStandard {...layoutProps} />);
    }
    // cards, timeline, showcase fall through to generic GameHomeLayout
  }

  // Progressive 50/50 dedicated layouts
  if (gameType === 'progressive5050') {
    const layoutProps = {
      mainDraw,
      bonusDraws,
      loading: combinedLoading,
      partner,
      onLoginClick,
      t,
      editMode,
      pendingChanges: {},
      isSaving,
      handleTranslationUpdate,
      handleSave,
      isAdmin,
      sections,
      updateSection,
      salesClosed,
      refetch,
    };

    if (layoutStyle === 'standard' || layoutStyle === 'style1') {
      return withSections(<Progressive5050StyleStandard {...layoutProps} />);
    }
    if (layoutStyle === 'premium-plus') {
      return withSections(<Classic5050StylePremiumPlus {...layoutProps} gameType={gameType} />);
    }
    if (layoutStyle === 'style2') {
      return withSections(<Progressive5050Style2 {...layoutProps} />);
    }
    if (layoutStyle === 'style3') {
      return withSections(<Progressive5050Style3 {...layoutProps} />);
    }
    // Default to Progressive Standard for any other style (charity falls through to GameHomeLayout)
    if (layoutStyle !== 'charity') {
      return withSections(<Progressive5050StyleStandard {...layoutProps} />);
    }
  }

  // Prize Draw dedicated layouts
  if (gameType === 'prizeDraw') {
    const layoutProps = {
      mainDraw,
      bonusDraws,
      prizes,
      loading: combinedLoading,
      partner,
      onLoginClick,
      t,
      editMode,
      pendingChanges: {},
      isSaving,
      handleTranslationUpdate,
      handleSave,
      isAdmin,
      sections,
      updateSection,
      salesClosed,
      refetch,
    };

    if (layoutStyle === 'style1') {
      return withSections(<PrizeDrawStyle1 {...layoutProps} />);
    }
    if (layoutStyle === 'style2') {
      return withSections(<PrizeDrawStyleGallerySpotlight {...layoutProps} />);
    }
    // standard falls through to PrizeDrawStyleStandard (charity falls through to GameHomeLayout)
    if (layoutStyle !== 'charity') {
      return withSections(<PrizeDrawStyleStandard {...layoutProps} />);
    }
  }

  // Square Game dedicated layouts
  if (gameType === 'squareGame') {
    const layoutProps = {
      mainDraw,
      bonusDraws,
      loading: combinedLoading,
      partner,
      onLoginClick,
      t,
      editMode,
      pendingChanges: {},
      isSaving,
      handleTranslationUpdate,
      handleSave,
      isAdmin,
      sections,
      updateSection,
      salesClosed,
      refetch,
    };

    if (layoutStyle === 'standard') {
      return withSections(<SquareGameStyleStandard {...layoutProps} />);
    }
    if (layoutStyle === 'gameday') {
      return withSections(<SquareGameStyleScoreboard {...layoutProps} />);
    }
    if (layoutStyle === 'charity') {
      return withSections(<SquareGameStyleCharity {...layoutProps} />);
    }
  }

  // Modified 50/50 dedicated layouts
  if (gameType === 'modified5050') {
    const layoutProps = {
      mainDraw,
      bonusDraws,
      loading: combinedLoading,
      partner,
      onLoginClick,
      t,
      editMode,
      pendingChanges: {},
      isSaving,
      handleTranslationUpdate,
      handleSave,
      isAdmin,
      sections,
      updateSection,
      salesClosed,
      gameType,
      refetch,
    };

    if (layoutStyle === 'style1') {
      return withSections(<Modified5050Style1 {...layoutProps} />);
    }
    // Default to standard for all other styles
    return withSections(<Modified5050StyleStandard {...layoutProps} />);
  }

  // Classic 50/50 dedicated layouts
  if (gameType === 'classic5050') {
    const layoutProps = {
      mainDraw,
      bonusDraws,
      loading: combinedLoading,
      partner,
      onLoginClick,
      t,
      editMode,
      pendingChanges: {},
      isSaving,
      handleTranslationUpdate,
      handleSave,
      isAdmin,
      sections,
      updateSection,
      salesClosed,
      gameType,
      refetch,
    };

    if (layoutStyle === 'standard') {
      return withSections(<Classic5050StyleStandard {...layoutProps} />);
    }
    if (layoutStyle === 'premium') {
      return withSections(<Classic5050StylePremium {...layoutProps} />);
    }
    if (layoutStyle === 'premium-plus') {
      return withSections(<Classic5050StylePremiumPlus {...layoutProps} />);
    }
    if (layoutStyle === 'style1') {
      return withSections(<Classic5050Style1 {...layoutProps} />);
    }
    if (layoutStyle === 'style2') {
      return withSections(<Classic5050Style2 {...layoutProps} />);
    }
    if (layoutStyle === 'style3') {
      return withSections(<Classic5050Style3 {...layoutProps} />);
    }
    if (layoutStyle === 'style4') {
      return withSections(<Classic5050Style4 {...layoutProps} />);
    }
    if (layoutStyle === 'fluid') {
      return withSections(<Classic5050StyleFluid {...layoutProps} />);
    }
    // style5 handled above, default to standard (charity falls through to GameHomeLayout)
    if (layoutStyle !== 'charity') {
      return withSections(<Classic5050StyleStandard {...layoutProps} />);
    }
  }

  return withSections(
    <GameHomeLayout
      loading={combinedLoading}
      sidebar={!isFullWidth ? renderSidebar() : null}
      footer={renderFooter()}
      className={layoutConfig.theme === 'dark' ? 'dark' : ''}
    >
      {/* Hero Section */}
      {renderHero()}

      {/* For full-width layouts, render everything in main area */}
      {isFullWidth ? (
        <div className="space-y-8">
          {/* Inline sidebar content for full-width - two column layout */}
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {isSectionVisible('live_draw') && (
              <LiveDrawCard
                draw={mainDraw}
                onLoginClick={onLoginClick}
                onBuyNowClick={handleBuyNowClick}
                onShareClick={handleShareClick}
                hasAdditionalDraws={bonusDraws.length > 0}
                editMode={editMode}
                onTranslationUpdate={handleTranslationUpdate}
                hideCountdown={false}
              />
            )}
            {partner && (
              <PartnerBanner
                partner={partner}
                editMode={editMode}
                onTranslationUpdate={handleTranslationUpdate}
              />
            )}
          </div>
          
          {/* Main content */}
          {renderMainContent()}
        </div>
      ) : (
        /* Standard 2/3 + 1/3 layout main content */
        renderMainContent()
      )}
    </GameHomeLayout>
  );
}

// PropTypes for documentation
GameHomePageInner.propTypes = {};

// Wrap with ErrorBoundary for resilient rendering
export default function GameHomePage(props) {
  return (
    <ErrorBoundary>
      <GameHomePageInner {...props} />
    </ErrorBoundary>
  );
}

// Default export with display name for debugging
GameHomePage.displayName = 'GameHomePage';
